import { useCallback } from 'react'

import { useMutation } from '@apollo/client/react'

import { graphql } from '~publish/graphql'

import { PostUnreadMentionCount } from './fragments/PostUnreadMentionCount'
import { PostUnreadNoteCount } from './fragments/PostUnreadNoteCount'
import { isOptionalNotesSummaryError } from './isOptionalNotesSummaryError'
import { useNotesReadAction } from './useNotesReadAction'
import type { NotesReadAction } from './useNotesReadAction'
import type { NotesReadState } from './useNotesReadState'

export const MarkNotesUnreadMutation = graphql(
  /* GraphQL */ `
    mutation MarkNotesUnread($input: MarkCollaborationNotesUnreadInput!) {
      markCollaborationNotesUnread(input: $input) {
        __typename
        ... on MarkCollaborationNotesUnreadSuccess {
          readState {
            readThroughNoteId
            firstUnreadNoteId
            unreadNoteCount
          }
          post {
            ...PostUnreadNoteCount
            ...PostUnreadMentionCount
          }
        }
      }
    }
  `,
  [PostUnreadNoteCount, PostUnreadMentionCount],
)

export function useMarkNotesUnread(
  postId: string,
  applyState: (state: NotesReadState) => void,
): NotesReadAction {
  const [mutate] = useMutation(MarkNotesUnreadMutation, { errorPolicy: 'all' })
  const write = useCallback(
    async (
      selectedPostId: string,
      fromNoteId: string,
    ): Promise<NotesReadState | null> => {
      const response = await mutate({
        variables: { input: { postId: selectedPostId, fromNoteId } },
      })
      const payload = response.data?.markCollaborationNotesUnread
      return (!response.error || isOptionalNotesSummaryError(response.error)) &&
        payload?.__typename === 'MarkCollaborationNotesUnreadSuccess'
        ? payload.readState
        : null
    },
    [mutate],
  )

  return useNotesReadAction(postId, applyState, write)
}
